import { all, call, takeLatest, takeLeading, put, select } from 'redux-saga/effects';
import {
  CloudUtils,
  CloudActions,
  CostCenterServiceUrls,
  CloudErrors,
  UserServiceUrls,
} from '@portal/portal-core';

import { AdminClientActions } from 'ducks/admin-client';
import { UserClientActions } from 'ducks/user-client';
import { existingInvitesFromClient } from 'ducks/user-client/sagas/invite-saga';
import * as FrontendUtils from 'utils';
import { AuthClientUtils } from 'ducks/auth-client';

// `../utils` is untyped JS; sendAction resolves as `any`.
import { sendAction } from '../utils';
// `../types` is an untyped .js module; its constants resolve as `any`.
import * as Types from '../types';
import {
  createCustomFieldResult,
  updateCustomFieldResult,
  updateCustomFieldOrderResult,
  updateUserCustomFieldValuesResult,
  updateInviteCustomFieldValuesResult,
} from '../actions';

// where the user client keeps its invites (see the user client sagas config in sagas/index.ts)
const USER_INVITES_STORE_PATH = 'userClient.userInvites.';

// Minimal shape of the config object threaded through to `sendAction` and URL builders.
// `getAuthToken` is required by `sendAction`'s config parameter.
interface CustomFieldsConfig {
  baseApiUrl?: string;
  getAuthToken: () => string | null | undefined;
  [key: string]: unknown;
}

// Worker sagas take `(config, action)` when driven by `takeLatest` (the trailing
// `timeout` default is never supplied by `takeLatest`). This alias drops the
// optional `timeout` param so redux-saga's `takeLatest` overload can bind `config`
// and append the matched action as the last argument.
type SagaWorker<A> = (
  config: CustomFieldsConfig,
  action: A,
) => Generator<unknown, void, unknown>;

// Wrapped response returned by `sendAction`.
interface SendActionResponse {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  data?: Record<string, unknown>;
  errorDetail?: unknown;
}

// A reference custom field, as read for the active-count guard.
interface CustomFieldReference {
  state?: string;
  [key: string]: unknown;
}

// The subset of a custom field key mutated by `updateCustomField`.
interface UpdateCustomFieldPayload {
  status?: string;
  required?: boolean;
  label?: string;
  state?: string;
  [key: string]: unknown;
}

interface CreateCustomFieldAction {
  type: string;
  userId: string;
  costCenterId: string;
  productAccessId: string;
  label: string;
}

interface UpdateCustomFieldAction {
  type: string;
  userId: string;
  costCenterId: string;
  productAccessId: string;
  referenceFields: CustomFieldReference[];
  id: string;
  payload: UpdateCustomFieldPayload;
}

interface UpdateCustomFieldOrderAction {
  type: string;
  userId: string;
  costCenterId: string;
  productAccessId: string;
  fields: Record<string, unknown>[];
}

interface UpdateCustomFieldValuesAction {
  type: string;
  costCenterId: string;
  productAccessId: string;
  userId: string;
  fields: unknown;
  isAdmin: boolean;
  adminId?: string;
}

interface PhantomCustomFieldValuesAction {
  type: string;
  productName: string;
  phantomToUserValues: unknown;
  userId: string;
  costCenterId: string;
  productAccessId: string;
  fields: unknown;
}

interface UpdateCustomFieldValuesWithInviteAction {
  type: string;
  inviteId: string;
  costCenterId: string;
  productAccessId: string;
  userId: string;
  fields: unknown;
  isAdmin: boolean;
  adminId?: string;
}

// Shape of the phantom-conversion API response consumed below.
interface PhantomResponseData {
  expired?: unknown;
  cancelled?: unknown;
  jwt?: string;
  adminEmail?: string;
  user?: {
    userId?: string;
    primaryEmail?: string;
  };
}

interface PhantomResponse {
  succeeded?: boolean;
  data?: PhantomResponseData;
  error?: unknown;
  errorDetail?: unknown;
}

interface ConvertPhantomResult {
  success: boolean;
  userDestinationUrl: string;
  jwt: string | undefined;
  errorDetail: unknown;
}

function* createCustomField(
  config: CustomFieldsConfig,
  action: CreateCustomFieldAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { userId, costCenterId, productAccessId, label } = action;
  try {
    const field = {
      id: CloudUtils.newUUID(),
      required: false,
      label,
      status: 'active',
    };
    const keys = [field];

    const serverAction = CloudActions.customFieldKeysCreate(costCenterId, productAccessId, keys);

    const url = CostCenterServiceUrls.customFieldsPath();
    const { succeeded, failed, error, data, errorDetail } = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SendActionResponse;

    if (failed) {
      const success = false;
      yield put(createCustomFieldResult(true, success, {}, error, errorDetail));
    } else if (succeeded) {
      yield put(AdminClientActions.reqAdminSyncForceUpdate(userId, costCenterId));
      yield put(CloudActions.syncUserAdminCostCenter(userId, data));
    }
  } catch (error) {
    const success = false;
    yield put(createCustomFieldResult(true, success, {}, error));
  }
}

function* updateCustomField(
  config: CustomFieldsConfig,
  action: UpdateCustomFieldAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { userId, costCenterId, productAccessId, referenceFields, id, payload } = action;

  const modifyableKeys = ['status', 'required', 'label'];

  try {
    if (Object.keys(payload).find((key) => !modifyableKeys.includes(key))) {
      throw new Error(`Only modifiable keys are "${modifyableKeys.join(', ')}"`);
    }

    // @TODO [FJCLOUD-1958, FJCLOUD-1959, FJCLOUD-1961] – toggling the state, required status
    // or label of a custom fields is currently handled by this single saga. Here's the entry
    // point for subbing in an API request

    // const referenceFields = yield select((state) => getCustomFieldKeysForAdmin(state));

    // verify that the user has available space for new custom fields
    if (
      payload.status &&
      payload.status === Types.CUSTOM_FIELD_STATES.ACTIVE &&
      referenceFields.filter(({ state }) => state === Types.CUSTOM_FIELD_STATES.ACTIVE).length ===
      10
    ) {
      // return fields as is if they can't activate an inactive or archived field.
      // The original JS passes the fields object as the `success` positional arg;
      // cast is type-only and preserves that exact runtime call.
      yield put(
        updateCustomFieldResult(true, {
          fields: [...referenceFields],
        } as unknown as boolean),
      );
    } else {
      // if toggling to an INACTIVE or ARCHIVED state, set required to false
      let updatedField;
      if (payload.state && payload.state !== Types.CUSTOM_FIELD_STATES.ACTIVE) {
        updatedField = {
          ...payload,
          required: false,
          id: action.id,
          updatedAt: CloudUtils.currentTime(),
        };
      } else {
        updatedField = {
          ...payload,
          id,
          updatedAt: CloudUtils.currentTime(),
        };
      }
      const keys = [updatedField];
      const serverAction = CloudActions.customFieldKeysUpdate(costCenterId, productAccessId, keys);

      const url = CostCenterServiceUrls.customFieldsPath();
      const { succeeded, failed, error, data, errorDetail } = (yield call(
        sendAction,
        config,
        serverAction,
        url,
        timeout,
      )) as SendActionResponse;

      if (succeeded) {
        yield put(AdminClientActions.reqAdminSyncForceUpdate(userId, costCenterId));
        yield put(CloudActions.syncUserAdminCostCenter(userId, data));
      } else if (failed) {
        const success = false;
        yield put(updateCustomFieldResult(true, success, {}, error, errorDetail));
      }
    }
  } catch (error) {
    const success = false;
    yield put(updateCustomFieldResult(true, success, {}, error));
  }
}

function* updateCustomFieldOrder(
  config: CustomFieldsConfig,
  action: UpdateCustomFieldOrderAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { userId, costCenterId, productAccessId, fields } = action;

  try {
    const keys = fields.map((f, i) => ({
      ...f,
      orderIndex: i + 1,
    }));
    const serverAction = CloudActions.customFieldKeysUpdate(costCenterId, productAccessId, keys);

    const url = CostCenterServiceUrls.customFieldsPath();
    const { succeeded, failed, error, data, errorDetail } = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SendActionResponse;

    if (succeeded) {
      yield put(AdminClientActions.reqAdminSyncForceUpdate(userId, costCenterId));
      yield put(CloudActions.syncUserAdminCostCenter(userId, data));
    } else if (failed) {
      const success = false;
      yield put(updateCustomFieldOrderResult(true, success, {}, error, errorDetail));
    }
  } catch (error) {
    const success = false;
    yield put(updateCustomFieldOrderResult(true, success, {}, error));
  }
}

function* updateCustomFieldValues(
  config: CustomFieldsConfig,
  action: UpdateCustomFieldValuesAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { costCenterId, productAccessId, userId, fields, isAdmin, adminId } = action;

  try {
    const serverAction = CloudActions.customFieldValuesCreateOrUpdate(
      costCenterId,
      productAccessId,
      userId,
      fields,
    );

    const url = CostCenterServiceUrls.customFieldsPath();

    const { succeeded, failed, error, data, errorDetail } = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SendActionResponse;

    if (succeeded) {
      const success = true;
      yield put(updateUserCustomFieldValuesResult(false, success, data));
    } else if (failed) {
      const success = false;
      yield put(updateUserCustomFieldValuesResult(true, success, data, error, errorDetail));
    }

    if (isAdmin) {
      // `isAdmin` implies `adminId` is present; cast is type-only.
      yield put(AdminClientActions.reqAdminSyncForceUpdate(adminId as string, costCenterId));
      yield put(CloudActions.syncUserAdminCostCenter(adminId, data));
    }
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('error', error);
  }
}

function* convertPhantomToUser(
  productName: string,
  phantomToUserValues: unknown,
  config: CustomFieldsConfig,
  timeout: number | undefined,
): Generator<unknown, ConvertPhantomResult, unknown> {
  const serverAction = CloudActions.convertPhantomUserToUser(phantomToUserValues);

  const phantomUrl = UserServiceUrls.convertPhantomUserUrl(config.baseApiUrl);

  const phantomResponse = (yield call(
    sendAction,
    config,
    serverAction,
    phantomUrl,
    timeout,
  )) as PhantomResponse;

  const { succeeded, data, error, errorDetail } = phantomResponse;

  let userDestinationUrl = 'no-redirect';

  if (succeeded) {
    // `succeeded` implies `data` is present; cast is type-only and preserves the
    // original direct property access (and its throw-on-undefined behavior).
    const phantomData = data as PhantomResponseData;
    // check if the user invite is expired or revoked
    if (phantomData.expired === CloudErrors.inviteUserErrorTypes.INVITE_EXPIRED) {
      userDestinationUrl = `/invite?${CloudErrors.inviteUserErrorTypes.INVITE_EXPIRED}&productName=${productName}&adminEmail=${phantomData.adminEmail}`;
    } else if (phantomData.cancelled === CloudErrors.inviteUserErrorTypes.INVITE_CANCELLED) {
      userDestinationUrl = `/invite?${CloudErrors.inviteUserErrorTypes.INVITE_CANCELLED}&productName=${productName}&adminEmail=${phantomData.adminEmail}`;
    } else if (!phantomData.jwt && phantomData.user) {
      // This is the case if the user's email was unable to be verified due to expired token.
      const { userId, primaryEmail } = phantomData.user;
      userDestinationUrl = `/?${CloudActions.EMAIL_VERIFICATION_UNVERIFIED}&userId=${userId}&emailAddress=${primaryEmail}`;
      yield put(CloudActions.emailVerificationTokenResend(userId, primaryEmail));
    } else {
      userDestinationUrl = `/?${CloudActions.CREATE_NEW_USER_SUCCESS}&productName=${productName}`;
    }
    return {
      success: true,
      userDestinationUrl,
      jwt: phantomData.jwt,
      errorDetail: '',
    };
  }

  // not successful means we should render an error in the UI
  const open = true;
  const success = false;
  // `data` is a domain shape without an index signature; cast is type-only.
  yield put(
    updateUserCustomFieldValuesResult(
      open,
      success,
      data as Record<string, unknown> | undefined,
      error,
      errorDetail,
    ),
  );

  return {
    success: false,
    userDestinationUrl,
    jwt: '',
    errorDetail,
  };
}

function* phantomCustomFieldValues(
  config: CustomFieldsConfig,
  action: PhantomCustomFieldValuesAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { productName, phantomToUserValues, fields } = action;

  try {
    // the values are saved by the same request that accepts the invite (FJCLOUD-4502)
    const { success, userDestinationUrl, jwt } = yield* convertPhantomToUser(
      productName,
      { ...(phantomToUserValues as object), customFieldValues: fields },
      config,
      timeout,
    );

    if (success) {
      // Expired or cancelled invites and expired email verification tokens convert without a jwt.
      // Navigate anyway so the user sees why, rather than a disabled form (FJCLOUD-4502).
      if (jwt) {
        // getAuthTokenKey() may be undefined; runtime keeps prior pass-through behavior.
        yield AuthClientUtils.setAuthJwtToken(AuthClientUtils.getAuthTokenKey() as string, jwt);
      }
      yield FrontendUtils.navigate(userDestinationUrl);
    }
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('error', error);
  }
}

function* updateCustomFieldValuesWithInvite(
  config: CustomFieldsConfig,
  action: UpdateCustomFieldValuesWithInviteAction,
  timeout = 5000,
): Generator<unknown, void, unknown> {
  const { inviteId, costCenterId, userId, fields, isAdmin, adminId } = action;

  try {
    // Accept the invite and save the values in one request, so they can't be lost between two
    // chained requests (FJCLOUD-4502).
    const serverAction = CloudActions.inviteUserProductAccessAccept(userId, inviteId, fields);
    const url = CostCenterServiceUrls.inviteUrl(config.baseApiUrl);

    const { succeeded, failed, error, data, errorDetail } = (yield call(
      sendAction,
      config,
      serverAction,
      url,
      timeout,
    )) as SendActionResponse;

    if (succeeded) {
      const success = true;
      yield put(updateInviteCustomFieldValuesResult(false, success, data));
      // The response is the updated cost center. Storing it now puts the user in the license, which
      // closes the form and shows the confirmation without waiting for the next sync.
      if (data?.id) {
        yield put(CloudActions.costCenters({ [data.id as string]: data }));
      }
      // same confirmation and invite list update as accepting an invite without custom fields
      yield put(UserClientActions.clientAcceptInviteResult(true, success, data));
      const invites = ((yield select(existingInvitesFromClient(USER_INVITES_STORE_PATH))) ||
        []) as { id: string }[];
      yield put(UserClientActions.clientInvitesSync(invites.filter(({ id }) => id !== inviteId)));

      if (isAdmin) {
        // `isAdmin` implies `adminId` is present; cast is type-only.
        yield put(AdminClientActions.reqAdminSyncForceUpdate(adminId as string, costCenterId));
        yield put(CloudActions.syncUserAdminCostCenter(adminId, data));
      }
    } else if (failed) {
      const success = false;
      yield put(updateInviteCustomFieldValuesResult(true, success, data, error, errorDetail));
    }
  } catch (error) {
    const success = false;
    yield put(updateInviteCustomFieldValuesResult(true, success, {}, error));
  }
}

function* createCustomFieldSaga(config: CustomFieldsConfig): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.CREATE_CUSTOM_FIELD,
    createCustomField as SagaWorker<CreateCustomFieldAction>,
    config,
  );
}

function* updateCustomFieldSaga(config: CustomFieldsConfig): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.UPDATE_CUSTOM_FIELD,
    updateCustomField as SagaWorker<UpdateCustomFieldAction>,
    config,
  );
}

function* updateCustomFieldOrderSaga(config: CustomFieldsConfig): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.UPDATE_CUSTOM_FIELD_ORDER,
    updateCustomFieldOrder as SagaWorker<UpdateCustomFieldOrderAction>,
    config,
  );
}

function* updateCustomFieldValuesSaga(
  config: CustomFieldsConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.UPDATE_CUSTOM_FIELD_VALUES,
    updateCustomFieldValues as SagaWorker<UpdateCustomFieldValuesAction>,
    config,
  );
}

function* updateCustomFieldValuesWithInviteSaga(
  config: CustomFieldsConfig,
): Generator<unknown, void, unknown> {
  // takeLeading: ignore a duplicate submit rather than cancelling the one already in flight
  yield takeLeading(
    Types.UPDATE_CUSTOM_FIELD_VALUES_WITH_INVITE,
    updateCustomFieldValuesWithInvite as SagaWorker<UpdateCustomFieldValuesWithInviteAction>,
    config,
  );
}

function* convertPhantomCustomFieldValues(
  config: CustomFieldsConfig,
): Generator<unknown, void, unknown> {
  yield takeLeading(
    Types.PROCESS_PHANTOM_INVITE_CUSTOM_FIELDS,
    phantomCustomFieldValues as SagaWorker<PhantomCustomFieldValuesAction>,
    config,
  );
}

// Runtime default stays an empty object; cast is type-only (real callers pass a
// fully-formed config with `getAuthToken`).
export default (config: CustomFieldsConfig = {} as CustomFieldsConfig) =>
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([
      createCustomFieldSaga(config),
      updateCustomFieldSaga(config),
      updateCustomFieldOrderSaga(config),
      updateCustomFieldValuesSaga(config),
      updateCustomFieldValuesWithInviteSaga(config),
      convertPhantomCustomFieldValues(config),
    ]);
  };
